import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Avatar from "@/components/base/Avatar";
import type { TugBrief, TugTeam } from "@/lib/types";
import {
  STATE_BADGE,
  STATE_BORDER,
  STATE_STRIP,
  TEAM_SOFT,
  TEAM_TEXT,
  formatCountdown,
  isDim,
  modeLabel,
  stateLabel,
  teamKey,
  teamName,
} from "../utils";

interface Props {
  brief: TugBrief;
  /** server_ts 기준 경과 초 (PC 시계를 믿지 않는다) */
  elapsed?: number;
}

// 목록 · 메인 공통 줄다리기 카드.
// state === "open" 이면 서버가 a_taps·b_taps 를 0 으로 주므로 점수는 «🔒 종료 후 공개» 로만 표시한다.
export default function TugCard({ brief, elapsed = 0 }: Props) {
  const [item, setItem] = useState<TugBrief>(brief);

  useEffect(() => {
    setItem(brief);
  }, [brief]);

  const open = item.state === "open";
  const remaining = Math.max(0, (item.end_in ?? 0) - elapsed);
  const cd = formatCountdown(remaining);
  const unlimited = item.limit_cnt === 0;
  const dim = isDim(item.state);
  const mine = item.is_mine === 1;
  const joined = item.is_joined === 1;
  const winTeam = teamKey(item.win_team);

  const teamBox = (t: TugTeam) => {
    const name = teamName(t === "A" ? item.team_a : item.team_b, t);
    const cnt = (t === "A" ? item.a_cnt : item.b_cnt) ?? 0;
    return (
      <div className={`rounded-lg border px-3 py-2 text-center ${TEAM_SOFT[t]}`}>
        <p className={`font-heading text-sm font-bold ${TEAM_TEXT[t]}`}>{name}</p>
        <p className="mt-0.5 text-sm text-foreground-600">
          <strong className="text-foreground-900">{cnt}</strong>
          {unlimited ? "명 · 제한 없음" : ` / ${item.limit_cnt}명`}
        </p>
      </div>
    );
  };

  return (
    <article
      id={`tug-${item.no}`}
      className={`relative flex h-full flex-col overflow-hidden rounded-xl border bg-background-50 ${
        STATE_BORDER[item.state]
      } ${dim ? "opacity-70" : ""}`}
    >
      <span
        className={`absolute inset-y-0 left-0 w-1.5 ${STATE_STRIP[item.state]}`}
        aria-hidden="true"
      ></span>

      <div className="flex flex-1 flex-col p-5 pl-6">
        {/* 상태 뱃지 */}
        <div className="flex flex-wrap items-center gap-2">
          <span
            className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-semibold ${
              STATE_BADGE[item.state]
            }`}
          >
            {stateLabel(item.state, item.state_txt)}
          </span>
          {mine && (
            <span className="inline-block rounded-full bg-accent-100 px-2.5 py-0.5 text-xs font-semibold text-accent-900">
              내가 연 경기
            </span>
          )}
          {joined && (
            <span className="inline-block rounded-full bg-primary-100 px-2.5 py-0.5 text-xs font-semibold text-primary-700">
              참가완료
            </span>
          )}
        </div>

        {/* 제목 */}
        <h3 className="mt-3 flex items-start gap-2 font-heading text-base font-bold leading-snug text-foreground-950">
          <span aria-hidden="true">🪢</span>
          <Link to={`/tug/${item.no}`} className="hover:text-primary-700">
            {item.subject}
          </Link>
        </h3>

        {/* 등록자 */}
        <div className="mt-2.5 flex items-center gap-2 text-sm text-foreground-600">
          <Avatar name={item.host?.name} photo={item.host?.photo} size={28} />
          <span>
            <strong className="font-semibold text-foreground-800">
              {item.host?.aho} {item.host?.name}
            </strong>{" "}
            님이 열었습니다
          </span>
        </div>

        {/* 팀별 인원 */}
        <div className="mt-3 grid grid-cols-2 gap-2">
          {teamBox("A")}
          {teamBox("B")}
        </div>

        {/* 보상 */}
        {item.has_reward === 1 && item.reward ? (
          <p className="mt-2.5 inline-flex items-center gap-1.5 text-sm text-foreground-700">
            <span aria-hidden="true">🎁</span>
            이긴 팀 : <strong className="font-semibold">{item.reward}</strong>
          </p>
        ) : null}

        {/* 마감 · 점수 공개 여부 */}
        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-sm">
          {open ? (
            <>
              <span className="inline-flex items-center gap-1.5 text-foreground-600">
                <i
                  className="ri-alarm-warning-line text-foreground-400"
                  aria-hidden="true"
                ></i>
                마감까지{" "}
                <strong
                  className={`tabular-nums ${
                    cd.urgent ? "animate-pulse text-accent-700" : "text-foreground-900"
                  }`}
                >
                  {cd.text}
                </strong>
              </span>
              <span className="inline-flex items-center gap-1.5 text-foreground-500">
                <i className="ri-lock-2-line text-foreground-400" aria-hidden="true"></i>
                점수는 종료 후 공개
              </span>
            </>
          ) : (
            <span className="inline-flex items-center gap-1.5 text-foreground-600">
              <i className="ri-trophy-line text-foreground-400" aria-hidden="true"></i>
              <strong className={`${TEAM_TEXT[winTeam]}`}>{item.win_name || "무승부"}</strong>
              {item.win_team === "D" ? "" : " 승리"}
              <span className="text-foreground-400">· {modeLabel(item.judge_mode, item.mode_label)}</span>
            </span>
          )}
        </div>

        {/* CTA */}
        <div className="mt-4 flex flex-1 items-end">
          <Link
            to={`/tug/${item.no}`}
            className={`inline-flex w-full items-center justify-center gap-1.5 whitespace-nowrap rounded-md px-4 py-2.5 text-sm font-bold transition-colors ${
              open
                ? "bg-primary-500 text-background-50 hover:bg-primary-600"
                : "border border-background-300 bg-background-50 text-foreground-700 hover:bg-background-100"
            }`}
          >
            {open ? (mine ? "경기 관리" : joined ? "기록 기다리기" : "참가하기") : "결과 보기"}
            <i className="ri-arrow-right-line text-base" aria-hidden="true"></i>
          </Link>
        </div>
      </div>
    </article>
  );
}